import styled from 'styled-components'
import PropTypes from 'prop-types'

import ColumnGridPanel from 'components/ColumnGridPanel'
import Tooltip from 'components/Tooltip'

import { percentage, friendlyName } from 'helpers/formatters'
import EnrolledIcon from 'pages/shared/EnrolledIcon'

import useServerlessDemandResponse from 'hooks/useServerlessDemandResponse'
import { useStoreContext } from 'hooks/StoreContext'

import RenderFirmwareStatus from 'pages/shared/RenderFirmwareStatus'

import { color } from 'styles/colors'

const propTypes = {
  report: PropTypes.shape().isRequired,
  deviceModel: PropTypes.string.isRequired
}

const Settings = ({ report, deviceModel }) => {
  const idsDisplayNames = { 'VERY_DRY': 'ON (HIGH)', 'MORE_DRY': 'ON (MED)', 'DRY': 'ON (LOW)', 'NONE': 'OFF' }
  const idsTitle = 'Integrated Dehumidification Solution: activates cooling and electric heat simultaneously, maintaining a preset in-door RH level.'

  const { defaultFirmwareLoads, areDefaultFirmwareLoadsLoaded } = useStoreContext()
  const { demandResponse } = useServerlessDemandResponse(report?.deviceId, true)

  const settingsReport = {
    firmwareVersion: report.firmwareVersion,
    fanMode: report.fanMode,
    rhHeatingSetpoint: percentage(report.rhHeatingSetpoint),
    rhCoolingSetpoint: percentage(report.rhCoolingSetpoint),
    ids: report.integratedDehumidificationConfig ? friendlyName(report.integratedDehumidificationUserEnabled ? report.userSetIdsLevel : 'NONE', idsDisplayNames) : null
  }

  const hasData = Object.values(settingsReport).some(val => val !== null)
  if (!hasData) {
    return null
  }

  const demandResponseTooltip = demandResponse && demandResponse.currentlyActive
    ? 'System is actively in a Utility Savings (Demand Response) event'
    : 'System is currently not in a Utility Savings (Demand Response) event'

  return (
    <ColumnGridPanel.Column>
      <ColumnGridPanel.Column.Header>Settings</ColumnGridPanel.Column.Header>
      <div>
        <S.Item label="Firmware" value={
          <RenderFirmwareStatus
            modelNumber={deviceModel}
            currentVersion={settingsReport.firmwareVersion}
            defaultFirmwareLoads={defaultFirmwareLoads}
            areDefaultFirmwareLoadsLoaded={areDefaultFirmwareLoadsLoaded}
            component="settings"
          />
        } hideIfValueIsNull
        />
      </div>
      <S.Item label="Fan Mode" value={settingsReport.fanMode} hideIfValueIsNull />
      <S.Item label="Htg RH Setpoint" value={settingsReport.rhHeatingSetpoint} hideIfValueIsNull />
      <S.Item label="Clg RH Setpoint" value={settingsReport.rhCoolingSetpoint} hideIfValueIsNull />
      {
        report.integratedDehumidificationConfig &&
        <S.Item
          label="IDS"
          value={settingsReport.ids}
          tooltip={idsTitle}
          hideIfValueIsNull
        />
      }
      {demandResponse && demandResponse.enrollmentStatus &&
        <S.DemandResponseItem label="Utility Savings Event" value={
          <S.DemandResponse>
            <Tooltip title={demandResponseTooltip}>
              <span>{demandResponse.currentlyActive ? 'ACTIVE' : 'NOT ACTIVE'}</span>
            </Tooltip>
            <EnrolledIcon showTooltip />
          </S.DemandResponse>
        } hideIfValueIsNull
        />
      }
    </ColumnGridPanel.Column>
  )
}

Settings.propTypes = propTypes

export default Settings

const S = {
  Item: styled(ColumnGridPanel.Column.Item)`
    grid-template-columns: 45% 55%;
  `,
  DemandResponseItem: styled(ColumnGridPanel.Column.Item)`
    grid-template-columns: 45% 55%;
    height: 22px;
  `,
  DemandResponse: styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
  `,
  FirmwareStatus: styled.div`
    display: inline-flex;
    align-items: center;
    color: ${color('successIcon')};
    font-size: 12px;
    margin-left: 5px;
  `,
  FirmwareContainer: styled.div`
    display: flex;
  `
}
